
/* GASSISA
=========================================================================================
 * Descripción: Generador ESC/POS del ticket personalizado de APARTADOS.
 *
 * Independiente del ticket personalizado de ventas.
 *
 * - Usa el esquema registrado de Parámetros de Caja.
 * - Conserva obligatoriamente los datos de control de mercancía.
 * - Mantiene el resumen financiero del apartado clásico.
 * - Distingue copia de almacenamiento y copia del cliente.
 * - No registra movimientos ni modifica inventarios.
 *
 * IMPORTANTE:
 * Este constructor sólo debe invocarse cuando
 * layoutTieneObjetos(parametrosCajaForm) sea true.
=========================================================================================
*/

import {
    layoutTieneObjetos,
    resolveTicketLayout,
    agruparObjetosPorRenglon,
} from "./ticketMotorLayout";

import {
    comandoSeccion,
    renderFilasEsquema,
    RESET_ESTADO_ESTILOS,
} from "./ticketEscPos";

import {
    valorCampoDatos,
    valorCampoDetalle,
    transliterarTexto,
} from "./ticketLayoutCatalogs";

import { DOTS_POR_MM } from "./ticketPerfil";

const numero = (valor) => {
    const resultado = Number(valor);
    return Number.isFinite(resultado) ? resultado : 0;
};

const dinero = (valor) => `$${numero(valor).toFixed(2)}`;

const texto = (valor) =>
    transliterarTexto(String(valor ?? "").trim());

const crearFilas = (objetos, obtenerValor) =>
    agruparObjetosPorRenglon(objetos).map((grupo) => ({
        items: grupo.objetos.map((objeto) => ({
            objeto,
            texto:
                objeto.tipo === "campo"
                    ? texto(obtenerValor(objeto.campo))
                    : texto(objeto.texto),
        })),
    }));

export const buildTicketApartadoPersonalizadoParts = ({
    cabecero = {},
    detalles = [],
    totales = {},
    parametrosCajaForm = {},
    titulo = "",
    esCopiaCliente = false,
}) => {
    if (!layoutTieneObjetos(parametrosCajaForm)) {
        throw new Error(
            "No existe un esquema personalizado registrado para esta caja."
        );
    }

    if (!cabecero.folioapartado) {
        throw new Error(
            "No es posible imprimir un apartado sin folio."
        );
    }

    if (!Array.isArray(detalles) || detalles.length === 0) {
        throw new Error(
            "No es posible imprimir un apartado sin artículos."
        );
    }

    const layout = resolveTicketLayout(parametrosCajaForm);
    const encoder = new TextEncoder();

    const partes = [
        encoder.encode("\x1B\x40"),
        encoder.encode("\x1B\x4B\x30"),
    ];

    const agregar = (contenido) => {
        partes.push(encoder.encode(contenido));
    };

    const linea = (contenido, opciones = {}) => {
        const alineacion = opciones.centrado
            ? "\x1B\x61\x01"
            : "\x1B\x61\x00";

        const negrita = opciones.negrita
            ? "\x1B\x45\x01"
            : "\x1B\x45\x00";

        agregar(
            `${alineacion}${negrita}${texto(contenido)}\x1B\x45\x00\n`
        );
    };

    const datoClave = (etiqueta, valor) => {
        agregar(
            "\x1B\x61\x00\x1B\x45\x01\x1B\x21\x10" +
            texto(`${etiqueta}: ${valor}`) +
            "\x1B\x21\x00\x1B\x45\x00\n"
        );
    };

    const imprimirSeccion = (objetos, obtenerValor) => {
        if (!objetos?.length) return;

        partes.push(
            ...renderFilasEsquema({
                textEncoder: encoder,
                filas: crearFilas(objetos, obtenerValor),
                dotsPorMm:
                    layout.calibracion?.dotsPorMm || DOTS_POR_MM,
                anchoCaracterDots:
                    layout.calibracion?.anchoCaracterDots || 9,
            })
        );
    };

    // ---------------------------------------------------------
    // 1. ENCABEZADO PERSONALIZADO
    // ---------------------------------------------------------

    const encabezadoConfigurado =
        parametrosCajaForm.encabezadoticket || "";

    if (encabezadoConfigurado.trim()) {
        const encabezado = comandoSeccion(
            encoder,
            layout.cabecero,
            texto(encabezadoConfigurado)
        );

        if (!encabezado.multilinea) {
            agregar("\x1B\x33\x00");
        }

        partes.push(encabezado.bytes);

        if (encabezado.requiereReset) {
            agregar(RESET_ESTADO_ESTILOS);
        }

        if (!encabezado.multilinea) {
            agregar("\x1B\x32");
        }
    }

    // ---------------------------------------------------------
    // 2. IDENTIFICACIÓN DEL COMPROBANTE
    // ---------------------------------------------------------

    linea(
        titulo ||
        (esCopiaCliente
            ? "COPIA CLIENTE"
            : "COPIA ALMACENAMIENTO"),
        { centrado: true, negrita: true }
    );

    linea("COMPROBANTE DE APARTADO", {
        centrado: true,
        negrita: true,
    });


    // ---------------------------------------------------------
    // 3. DATOS PERSONALIZADOS DE CAJA
    // ---------------------------------------------------------

    const datosApartado = {
        ...cabecero,
        fechaventa: cabecero.fecharegistro || "",
        razonsocial: cabecero.nombrecliente || "No especificado",
        remision: cabecero.folioapartado,
        tipopago: "APARTADO",
    };

    if (layout.datos.objetos.length > 0) {
        imprimirSeccion(
            layout.datos.objetos,
            (campo) => valorCampoDatos(campo, datosApartado)
        );
    }
    
    // Los datos de caja, vendedor, fecha, cliente, concepto y grupo
    // se imprimen mediante el diseño personalizado de Parámetros de Caja.
    // No repetirlos aquí.

    // Bloque obligatorio de control, incluso si el diseñador
    // no contiene estos campos.
    // linea(`Caja: ${cabecero.pc || ""}`);
    // linea(`Vendedor: ${cabecero.usuario || ""}`);
    // datoClave("Fecha", cabecero.fecharegistro || "");
    // datoClave(
    //     "Cliente",
    //     cabecero.nombrecliente || "No especificado"
    // );

    // if (cabecero.concepto) {
    //     datoClave("Concepto", cabecero.concepto);
    // }

    // if (cabecero.grupo) {
    //     datoClave("Grupo", cabecero.grupo);
    // }


    // ---------------------------------------------------------
    // 4. MERCANCÍA APARTADA
    // ---------------------------------------------------------

    linea("MERCANCIA APARTADA", {
        centrado: true,
        negrita: true,
    });

    detalles.forEach((articulo, indice) => {
        const detalleNormalizado = {
            ...articulo,
            costounitario: numero(
                articulo.pUnit ?? articulo.costounitario
            ),
            descuento: numero(
                articulo.desc ?? articulo.descuento
            ),
            total: numero(
                articulo.importeVenta ?? articulo.total
            ),
            ivaabono: numero(
                articulo.ivaPartida ?? articulo.ivaabono
            ),
        };

        // Imprimir exclusivamente los campos configurados
        // en el diseñador de Parámetros de Caja.
        if (layout.detalle.objetos.length > 0) {
            imprimirSeccion(
                layout.detalle.objetos,
                (campo) => valorCampoDetalle(campo, detalleNormalizado)
            );
        }

    });

    // ---------------------------------------------------------
    // 5. ESTADO DE CUENTA DEL APARTADO
    //
    // Se preservan los mismos conceptos y cálculos
    // del constructor clásico.
    // NO se utiliza el bloque de totales de ventas.
    // ---------------------------------------------------------


    linea("ESTADO DE CUENTA", {
        centrado: true,
        negrita: true,
    });

    const totalArticulos = numero(
        totales.totalImporteArticulos
    );

    const totalAbonado = numero(totales.totalAbonado);

    const restante = totalArticulos - totalAbonado;

    linea(
        `Subtotal abonos: ${dinero(totales.subTotal)}`
    );

    linea(
        `IVA abonos: ${dinero(totales.totalIVA)}`
    );

    linea(
        `Total articulos: ${dinero(totalArticulos)}`
    );

    linea(
        `Total abonado: ${dinero(totalAbonado)}`,
        { negrita: true }
    );

    linea(
        `Restante: ${dinero(restante)}`,
        { negrita: true }
    );



    linea("Total en letra:");

    linea(cabecero.montoabonoletra || "");


    // ---------------------------------------------------------
    // 6. LEYENDAS DE SEGURIDAD - COPIA CLIENTE
    // ---------------------------------------------------------

    if (esCopiaCliente) {
        linea("AVISO IMPORTANTE:", {
            centrado: true,
            negrita: true,
        });

        linea("Vigencia apartado: 15 dias.");
        linea("Ticket fisico indispensable.");

        linea("NO SE ACEPTAN DEVOLUCIONES.", {
            negrita: true,
        });

        linea("Articulos sin uso y c/etiqueta.");
        linea("No aplica en ofertas/promociones.");
        linea("Conserve este ticket fisico.");
    }

    // ---------------------------------------------------------
    // 7. PIE PERSONALIZADO
    // ---------------------------------------------------------

    const pieConfigurado =
        parametrosCajaForm.pieticket || "";

    if (pieConfigurado.trim()) {
        const pie = comandoSeccion(
            encoder,
            layout.pie,
            texto(pieConfigurado)
        );

        partes.push(pie.bytes);

        if (pie.requiereReset) {
            agregar(RESET_ESTADO_ESTILOS + "\n");
        }

    }

    // ---------------------------------------------------------
    // 8. FINALIZACIÓN Y CORTE
    // ---------------------------------------------------------

    linea("Vuelva pronto", {
        centrado: true,
    });

    agregar("\n\n\n");
    
    agregar("\n\n\n\n\n");

    agregar("\x1D\x56\x00");

    return { partes };
};
